import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isSupervisorOrAbove } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CertUploadDialog } from "@/components/CertUploadDialog";
import { CheckCircle, Warning, UploadSimple, Eye, X, CaretRight, FileXls } from "@phosphor-icons/react";

const STATUS_STYLE = {
  missing:   "bg-red-100 text-red-800 border-red-300",
  submitted: "bg-amber-100 text-amber-800 border-amber-300",
  approved:  "bg-emerald-100 text-emerald-800 border-emerald-300",
};

export default function Compliance() {
  const { user } = useAuth();
  const isSup = isSupervisorOrAbove(user);
  const [tab, setTab] = useState(isSup ? "dashboard" : "me");

  return (
    <AppLayout title="Member Paperwork & Compliance" subtitle="Required documents by certification — uploads route to Documents and update here automatically">
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="rounded-sm bg-slate-100 mb-4">
          <TabsTrigger value="me" data-testid="tab-me" className="rounded-sm">My Paperwork</TabsTrigger>
          {isSup && <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="rounded-sm">Member Compliance Dashboard</TabsTrigger>}
        </TabsList>
        <TabsContent value="me"><MyCompliance /></TabsContent>
        {isSup && <TabsContent value="dashboard"><Dashboard /></TabsContent>}
      </Tabs>
    </AppLayout>
  );
}

function ProgressBar({ percent }) {
  const color = percent === 100 ? "bg-emerald-600" : percent > 50 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="w-full bg-slate-100 h-2 rounded-sm overflow-hidden">
      <div className={`${color} h-full transition-all`} style={{ width: `${percent}%` }} />
    </div>
  );
}

function ComplianceView({ data, onReload, asSelf = false }) {
  const [uploadFor, setUploadFor] = useState(null); // {document_type, multi}
  const [collapsed, setCollapsed] = useState({});   // section -> bool
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;

  const { user, sections, summary } = data;
  const mandatorySections = (sections || []).filter((s) => s.mandatory);
  const optionalSections  = (sections || []).filter((s) => !s.mandatory);

  function SectionTable({ sec }) {
    const isCollapsed = collapsed[sec.section] ?? !sec.mandatory;
    return (
      <div className="surface-grid" data-testid={`section-${sec.section}`}>
        <button
          type="button"
          onClick={() => setCollapsed({ ...collapsed, [sec.section]: !isCollapsed })}
          className="w-full px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between hover:bg-slate-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <span className="font-display font-bold text-left">{sec.section}</span>
            {sec.mandatory ? (
              <Badge className="rounded-sm bg-slate-900 text-white text-[10px]">REQUIRED</Badge>
            ) : (
              <Badge className="rounded-sm bg-slate-200 text-slate-700 text-[10px]">OPTIONAL</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="tab-tag text-slate-500">{sec.items.length} item(s)</span>
            <CaretRight size={14} weight="bold" className={`text-slate-500 transition-transform ${isCollapsed ? "" : "rotate-90"}`} />
          </div>
        </button>
        {!isCollapsed && (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200">
              <tr className="text-left tab-tag text-slate-500">
                <th className="px-4 py-2">Document</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Files</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sec.items.map((it) => (
                <tr key={it.document_type} className="border-b border-slate-100" data-testid={`row-${it.document_type}`}>
                  <td className="px-4 py-2 font-medium">
                    {it.document_type}
                    {it.optional && <span className="ml-2 tab-tag text-slate-400">OPTIONAL</span>}
                    {it.multi && <Badge className="ml-2 rounded-sm bg-blue-600 text-white text-[10px]">MULTI</Badge>}
                  </td>
                  <td className="px-4 py-2">
                    <Badge className={`rounded-sm border ${STATUS_STYLE[it.status]} ${it.optional && it.status === "missing" ? "opacity-50" : ""}`}>
                      {it.status.toUpperCase()}
                    </Badge>
                  </td>
                  <td className="px-4 py-2">
                    {it.files.length === 0 ? <span className="text-slate-400 text-xs">—</span> : (
                      <div className="space-y-1">
                        {it.files.map((f) => (
                          <div key={f.id} className="flex items-center gap-2 text-xs">
                            <a href={`${API_BASE}/documents/${f.id}/download`} target="_blank" rel="noreferrer" className="mono underline text-slate-700 truncate max-w-[260px]">{f.filename}</a>
                            <Badge className={`rounded-sm text-[9px] border ${STATUS_STYLE[f.status === "approved" ? "approved" : "submitted"]}`}>{f.status?.toUpperCase()}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {asSelf && (
                      <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => setUploadFor(it)} data-testid={`btn-upload-${it.document_type}`}>
                        <UploadSimple size={12} weight="bold" className="mr-1" />UPLOAD
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="surface-grid p-4 grid grid-cols-1 md:grid-cols-4 gap-4 items-center" data-testid="compliance-summary">
        <div>
          <div className="tab-tag text-slate-500">Member</div>
          <div className="font-display font-bold text-slate-900 text-lg">{user?.name}</div>
          <div className="text-xs text-slate-500">{user?.email}</div>
          <div className="mt-1 flex flex-wrap gap-1">
            {(user?.certifications || []).map((c) => <Badge key={c} className="rounded-sm bg-slate-900 text-white text-[10px]">{c}</Badge>)}
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-1">
            <span className="tab-tag text-slate-500">Compliance · required documents only</span>
            <span className="mono text-sm font-bold text-slate-900">{summary?.percent_complete ?? 0}%</span>
          </div>
          <ProgressBar percent={summary?.percent_complete ?? 0} />
          <div className="text-[11px] text-slate-500 mt-1">
            {summary?.total_submitted}/{summary?.total_required} required documents submitted
            {summary?.missing_count > 0 && <> · <span className="text-red-600 font-semibold">{summary.missing_count} missing</span></>}
          </div>
        </div>
        <div>
          <Badge className={`rounded-sm border ${STATUS_STYLE[summary?.status] || "bg-slate-100 text-slate-700 border-slate-300"} text-[11px]`} data-testid="status-overall">
            {(summary?.status || "missing").toUpperCase()}
          </Badge>
        </div>
      </div>

      {/* Required sections — expanded by default */}
      <div className="space-y-3">
        {mandatorySections.map((sec) => <SectionTable key={sec.section} sec={sec} />)}
      </div>

      {/* Optional sections — collapsed by default, any member may upload */}
      {optionalSections.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-slate-500 mt-4 mb-1 px-1">
            <strong>Additional document types you may upload</strong> — not required for your current certifications, but available if you have them.
          </div>
          {optionalSections.map((sec) => <SectionTable key={sec.section} sec={sec} />)}
        </div>
      )}

      <CertUploadDialog open={!!uploadFor} category={uploadFor?.document_type} onClose={() => setUploadFor(null)} onDone={onReload} />
    </div>
  );
}

function MyCompliance() {
  const [data, setData] = useState(null);
  async function load() { const { data } = await api.get("/compliance/me"); setData(data); }
  useEffect(() => { load(); }, []);
  return <ComplianceView data={data} onReload={load} asSelf />;
}

function Dashboard() {
  const [rows, setRows] = useState([]);
  const [openUser, setOpenUser] = useState(null);
  const [pfData, setPfData] = useState(null);
  const [search, setSearch] = useState("");

  async function load() { const { data } = await api.get("/compliance/dashboard"); setRows(data); }
  useEffect(() => { load(); }, []);

  async function openMember(u) {
    setOpenUser(u);
    setPfData(null);
    const { data } = await api.get(`/compliance/personnel-file/${u.id}`);
    setPfData(data);
  }

  const filtered = rows.filter((r) =>
    !search || r.user.name.toLowerCase().includes(search.toLowerCase()) || r.user.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Input placeholder="Search member…" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-sm w-72" data-testid="search-member" />
        <Button asChild variant="outline" className="rounded-sm h-9 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="btn-export-xlsx">
          <a href={`${API_BASE}/compliance/export.xlsx`} target="_blank" rel="noreferrer">
            <FileXls size={14} weight="bold" className="mr-1" />DOWNLOAD EXCEL
          </a>
        </Button>
        <div className="ml-auto flex items-center gap-3 text-xs text-slate-600">
          <span className="flex items-center gap-1"><CheckCircle size={14} weight="bold" className="text-emerald-600" /> {filtered.filter((r) => r.summary.status === "complete").length} complete</span>
          <span className="flex items-center gap-1"><Warning size={14} weight="bold" className="text-amber-600" /> {filtered.filter((r) => r.summary.status === "partial").length} partial</span>
          <span className="flex items-center gap-1"><X size={14} weight="bold" className="text-red-600" /> {filtered.filter((r) => r.summary.status === "missing").length} missing</span>
        </div>
      </div>

      <div className="surface-grid">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left tab-tag text-slate-500">
              <th className="px-4 py-2">Member</th>
              <th className="px-4 py-2">Certifications</th>
              <th className="px-4 py-2">Compliance</th>
              <th className="px-4 py-2">Missing</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-6 text-slate-500">No members.</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.user.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openMember(r.user)} data-testid={`dash-row-${r.user.id}`}>
                <td className="px-4 py-2">
                  <div className="font-medium text-slate-900">{r.user.name}</div>
                  <div className="text-xs text-slate-500">{r.user.email}</div>
                </td>
                <td className="px-4 py-2">
                  <div className="flex flex-wrap gap-1">
                    {(r.user.certifications || []).map((c) => <Badge key={c} className="rounded-sm bg-slate-700 text-white text-[10px]">{c}</Badge>)}
                  </div>
                </td>
                <td className="px-4 py-2 w-72">
                  <ProgressBar percent={r.summary.percent_complete} />
                  <div className="text-[11px] mono text-slate-500 mt-1">{r.summary.total_submitted}/{r.summary.total_required} · {r.summary.percent_complete}%</div>
                </td>
                <td className="px-4 py-2 mono text-xs">{r.summary.missing_count}</td>
                <td className="px-4 py-2">
                  <Badge className={`rounded-sm border ${STATUS_STYLE[r.summary.status]}`}>{r.summary.status.toUpperCase()}</Badge>
                </td>
                <td className="px-4 py-2 text-right text-slate-400"><CaretRight size={16} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!openUser} onOpenChange={(o) => { if (!o) { setOpenUser(null); setPfData(null); } }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="personnel-file">
          <DialogHeader className="pb-3 border-b border-slate-200">
            <DialogTitle className="font-display text-xl">Personnel File · {openUser?.name}</DialogTitle>
            <p className="text-xs text-slate-500 mt-1">Bedford Stuyvesant Volunteer Ambulance Corps</p>
          </DialogHeader>
          <PersonnelFile data={pfData} reload={async () => { if (openUser) { const { data } = await api.get(`/compliance/personnel-file/${openUser.id}`); setPfData(data); } load(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PersonnelFile({ data, reload }) {
  if (!data) return <div className="text-sm text-slate-500 py-6">Loading personnel file…</div>;
  const { user, certifications, disciplinary_actions, medical_records, required_checklist } = data;

  return (
    <div className="space-y-6 pt-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 surface-grid p-4">
        <div>
          <div className="tab-tag text-slate-500">Member</div>
          <div className="font-display font-bold text-slate-900 text-lg">{user.name}</div>
          <div className="text-xs text-slate-500">{user.email}</div>
          {user.phone && <div className="text-xs text-slate-500 mono">{user.phone}</div>}
        </div>
        <div className="md:col-span-2">
          <div className="tab-tag text-slate-500 mb-1">Certifications</div>
          <div className="flex flex-wrap gap-1">
            {(user.certifications || []).map((c) => <Badge key={c} className="rounded-sm bg-slate-900 text-white text-[10px]">{c}</Badge>)}
            {(!user.certifications || user.certifications.length === 0) && <span className="text-xs text-slate-400">None on file</span>}
          </div>
          <div className="tab-tag text-slate-500 mt-3 mb-1">Required documents for this member <span className="text-[10px] text-slate-400">(asterisks below mark applicable items)</span></div>
          <div className="flex flex-wrap gap-1">
            {required_checklist.map((c) => (
              <Badge key={c.document_type} className={`rounded-sm text-[10px] ${c.submitted ? "bg-emerald-100 text-emerald-800 border-emerald-300 border" : "bg-red-50 text-red-700 border-red-200 border"}`}>
                {c.submitted ? "✓" : "○"} {c.document_type}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      <PersonnelSection title="Certifications" testid="pf-certifications" items={certifications} renderRow={(d) => (
        <>
          <td className="px-4 py-2 font-medium">
            {d.applicable_to_member && <span className="text-amber-600 font-bold mr-1" title="Required for this member's certification level">*</span>}
            {d.category}
          </td>
          <td className="px-4 py-2 mono text-xs truncate max-w-[260px]">{d.filename}</td>
          <td className="px-4 py-2 mono text-xs">{new Date(d.uploaded_at).toLocaleDateString()}</td>
          <td className="px-4 py-2"><Badge className="rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px]">{(d.status || "approved").toUpperCase()}</Badge></td>
          <td className="px-4 py-2 text-right">
            <a href={`${API_BASE}/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="text-xs underline">View</a>
          </td>
        </>
      )} cols={["Document", "File", "Uploaded", "Status", ""]} emptyMsg="No certifications uploaded yet." />

      <PersonnelSection title="Disciplinary Actions" testid="pf-disciplinary" items={disciplinary_actions} renderRow={(r) => (
        <>
          <td className="px-4 py-2 mono">{r.date || "—"}</td>
          <td className="px-4 py-2"><Badge className={`rounded-sm text-[10px] ${r.kind === "writeup" ? "bg-red-600 text-white" : "bg-amber-600 text-white"}`}>{r.kind.toUpperCase()}</Badge></td>
          <td className="px-4 py-2 font-medium">{r.category}</td>
          <td className="px-4 py-2 text-xs text-slate-600 truncate max-w-[320px]">{r.summary}</td>
          <td className="px-4 py-2">{r.author_name}</td>
          <td className="px-4 py-2 text-right">
            <a href={`${process.env.REACT_APP_BACKEND_URL}${r.pdf_url}`} target="_blank" rel="noreferrer" className="text-xs underline">PDF</a>
          </td>
        </>
      )} cols={["Date", "Type", "Category", "Summary", "Issued By", ""]} emptyMsg="No disciplinary actions on file." />

      <PersonnelSection title="Medical Records" testid="pf-medical" items={medical_records} renderRow={(d) => (
        <>
          <td className="px-4 py-2 font-medium">
            {d.applicable_to_member && <span className="text-amber-600 font-bold mr-1" title="Required for this member's certification level">*</span>}
            {d.category}
          </td>
          <td className="px-4 py-2 mono text-xs truncate max-w-[260px]">{d.filename}</td>
          <td className="px-4 py-2 mono text-xs">{new Date(d.uploaded_at).toLocaleDateString()}</td>
          <td className="px-4 py-2"><Badge className="rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px]">{(d.status || "approved").toUpperCase()}</Badge></td>
          <td className="px-4 py-2 text-right">
            <a href={`${API_BASE}/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="text-xs underline">View</a>
          </td>
        </>
      )} cols={["Document", "File", "Uploaded", "Status", ""]} emptyMsg="No medical records on file." />

      <p className="text-[11px] text-slate-500 italic">
        <span className="text-amber-600 font-bold">*</span> indicates the document is required for this member's certification level.
      </p>
    </div>
  );
}

function PersonnelSection({ title, items, testid, renderRow, cols, emptyMsg }) {
  return (
    <div className="surface-grid" data-testid={testid}>
      <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="font-display font-bold">{title}</div>
        <div className="tab-tag text-slate-500">{items.length} item{items.length === 1 ? "" : "s"}</div>
      </div>
      {items.length === 0 ? (
        <div className="px-4 py-6 text-slate-500 text-sm text-center">{emptyMsg}</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-white border-b border-slate-200">
            <tr className="text-left tab-tag text-slate-500">
              {cols.map((c, i) => <th key={i} className={`px-4 py-2 ${i === cols.length - 1 ? "text-right" : ""}`}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`${testid}-row-${item.id}`}>
                {renderRow(item)}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
